<template>
  <UserProfileStepper
    :user="formUser"
    :active-page="activePage"
    :name="$auth.user.name"
    :saving="savingUser || savingUserOrganisation || loadingUser"
    :errors="$v.formUser"
    :postcode-img="postcodeImg"
    :contact-img="contactImg"
    :loading="loadingUserOrganisations"
    :selected-organisation="selectedOrganisation"
    :user-organisations="organisationList"
    :valid-pages="validPages"
    :display-dots="!userRequiresEmployerSetup"
    :support-number="theme().novatedBrand?.support?.phone"
    :contact-us-key="$Constants.USER_PROFILE_PAGE_CONTACT_US"
    :progress-page="progressPage"
    :show-close-page="showCloseButton"
    @update:page="progressPage = $event"
    @skip="skip"
    @close="close"
    @update:user="formUser = $event"
    @update:organisation="selectedOrganisation = $event"
    @next="saveAndNext"
  />
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { getAsset } from 'ecx.saigon.ui.common';
import validations from './validations';
import resolveLocalRoute from '@/utils/resolve-local-route';

export default {
  name: 'UserProfileStepperWidget',
  data() {
    return {
      progressPage: this.$Constants.USER_PROFILE_PAGES.corporateEmail,
      formUser: { ...this.$Constants.INITIAL_USER },
      selectedOrganisation: { value: undefined, label: undefined },
      postcodeImg: undefined,
      contactImg: undefined
    };
  },
  validations,
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      loadingUser: (state) => state.user.loadingUser,
      userOrganisations: (state) => state.user.userOrganisations,
      loadingUserOrganisations: (state) => state.user.loadingUserOrganisations,
      savingUserOrganisation: (state) => state.user.savingUserOrganisation,
      name: (state) => state.user.name,
      savingUser: (state) => state.user.savingUser
    }),
    ...mapGetters('user', [
      'profileSetupPage',
      'userHasMultipleOrgMatches',
      'userRequiresEmployerSetup',
      'theme',
      'userIsAccessLevel'
    ]),
    ...mapGetters('application', ['creditCheckComplete']),
    organisationList() {
      return this.userOrganisations.map((org) => ({
        value: org.orgId,
        label: org.organisation
      }));
    },
    disableOrgSelection() {
      return this.user.hasOrganisation || !this.userHasMultipleOrgMatches;
    },
    organisationSelectionActive() {
      return (
        this.progressPage ===
        this.$Constants.USER_PROFILE_PAGES.organisationSelection
      );
    },
    validPages() {
      const pages = this.$Constants.USER_PROFILE_PAGES;
      return {
        [pages.corporateEmail]: this.$v[pages.corporateEmail].$valid,
        [pages.companyName]: this.$v[pages.companyName].$valid,
        [pages.organisationSelection]:
          this.$v[pages.organisationSelection].$valid &&
          !this.disableOrgSelection,
        [pages.postcode]: this.$v[pages.postcode].$valid,
        [pages.contactNumber]: this.$v[pages.contactNumber].$valid
      };
    },
    activePage() {
      return this.userRequiresEmployerSetup
        ? this.$Constants.USER_PROFILE_PAGE_CONTACT_US
        : this.progressPage;
    },
    showCloseButton() {
      return this.activePage !== 'corporateEmail';
    }
  },
  watch: {
    progressPage() {
      if (this.organisationSelectionActive && !this.userOrganisations.length) {
        this.getUserOrganisations();
      }
    }
  },
  methods: {
    ...mapActions('user', [
      'getUser',
      'saveUserProfile',
      'getUserOrganisations',
      'updateUserOrganisation'
    ]),
    skip() {
      if (!this.savingUser) {
        this.goToNext();
      }
    },
    close() {
      if (!this.savingUser) {
        this.$emit('close');
      }
    },
    async saveAndNext() {
      this.$v[this.progressPage].$touch();
      if (this.$v[this.progressPage].$invalid) {
        return;
      }

      this.organisationSelectionActive
        ? await this.updateUserOrganisation(this.selectedOrganisation.value)
        : await this.saveUserProfile(this.formUser);

      const profile = this.user.profile;
      if (profile.companyEmail && !profile.corporateEmailVerified) {
        return;
      }

      this.goToNext();
    },
    async goToNext() {
      const pageArray = Object.values(this.$Constants.USER_PROFILE_PAGES);
      const currentPageIndex = pageArray.indexOf(this.progressPage);
      let nextPage = pageArray[currentPageIndex + 1];

      if (
        nextPage === this.$Constants.USER_PROFILE_PAGES.organisationSelection &&
        this.disableOrgSelection
      ) {
        nextPage = pageArray[currentPageIndex + 2];
      }
      if (currentPageIndex === pageArray.length - 1) {
        await this.getUser(true);
        const hasCreditOnlyRestriction = this.userIsAccessLevel([
          this.$Constants.USER_ACCESS_LEVEL.CREDIT_ONLY
        ]);
        if (hasCreditOnlyRestriction && !this.creditCheckComplete) {
          const env = await getEnv();
          return (window.location = resolveLocalRoute(
            env.VUE_APP_CREDIT_CHECK_ROUTE
          ));
        }
        this.close();
      } else {
        this.progressPage = nextPage;
      }
    }
  },
  async mounted() {
    this.progressPage = this.profileSetupPage;
    this.formUser = { ...this.user.profile };

    this.postcodeImg = await getAsset(
      this.$Constants.PROFILE_SETUP_POSTCODE_PATH
    );
    this.contactImg = await getAsset(
      this.$Constants.PROFILE_SETUP_CONTACT_PATH
    );
  },
  components: {
    UserProfileStepper: () =>
      import('../../components/organism/user-profile-stepper')
  }
};
</script>
